import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Checkbox } from '../components/ui/checkbox';
import { CalendarIcon, Trash2, AlertTriangle } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { it } from 'date-fns/locale';
import { cn } from '../lib/utils';
import { generateId } from '../data/sampleData';
import { STANDARD_TEMPLATE_TASKS } from '../data/projectTemplates';

const PROJECT_COLORS = [
  { value: '#4F46E5', label: 'Indigo' },
  { value: '#7C3AED', label: 'Viola' },
  { value: '#059669', label: 'Smeraldo' },
  { value: '#DC2626', label: 'Rosso' },
  { value: '#EA580C', label: 'Arancione' },
  { value: '#0891B2', label: 'Ciano' },
  { value: '#4338CA', label: 'Blu Scuro' },
  { value: '#BE185D', label: 'Rosa' },
];

export const ProjectDialog = ({ open, onOpenChange, project, macroProjects, onSave, onDelete, canEditTasks = true }) => {
  const [formData, setFormData] = useState({
    name: '',
    startDate: '',
    endDate: '',
    color: PROJECT_COLORS[0].value,
    macroProjectId: '',
    saleAmount: '',
    estimatedRealDays: ''
  });
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [templateType, setTemplateType] = useState('standard');
  const [customTemplateTasks, setCustomTemplateTasks] = useState(STANDARD_TEMPLATE_TASKS);

  useEffect(() => {
    if (project) {
      setFormData({
        name: project.name,
        startDate: project.startDate,
        endDate: project.endDate,
        color: project.color,
        macroProjectId: project.macroProjectId || '',
        saleAmount: project.saleAmount !== undefined && project.saleAmount !== null ? String(project.saleAmount) : '',
        estimatedRealDays: project.estimatedRealDays !== undefined && project.estimatedRealDays !== null ? String(project.estimatedRealDays) : ''
      });
    } else {
      setFormData({
        name: '',
        startDate: '',
        endDate: '',
        color: PROJECT_COLORS[0].value,
        macroProjectId: '',
        saleAmount: '',
        estimatedRealDays: ''
      });
    }
    setTemplateType('standard');
    setCustomTemplateTasks(STANDARD_TEMPLATE_TASKS);
    setShowDeleteConfirm(false);
  }, [project, open]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.name || !formData.startDate || !formData.endDate) return;

    const payload = {
      id: project?.id || generateId('p'),
      name: formData.name,
      startDate: formData.startDate,
      endDate: formData.endDate,
      color: formData.color,
      macroProjectId: formData.macroProjectId || null,
      saleAmount: formData.saleAmount === '' ? null : Number(formData.saleAmount),
      estimatedRealDays: formData.estimatedRealDays === '' ? null : Number(formData.estimatedRealDays)
    };

    if (!project) {
      payload.templateType = templateType;
      payload.templateTasks = templateType === 'standard' ? STANDARD_TEMPLATE_TASKS : customTemplateTasks;
    }

    onSave(payload);
    onOpenChange(false);
  };

  const handleDelete = function() {
    if (onDelete && project) {
      onDelete(project.id);
      onOpenChange(false);
    }
  };

  const isEditing = !!project;
  const showTemplateOptions = !isEditing && canEditTasks;
  const customTemplateInvalid = showTemplateOptions && templateType === 'custom' && customTemplateTasks.length === 0;

  const handleToggleCustomTask = function(taskName, isChecked) {
    setCustomTemplateTasks(function(prev) {
      const nextSet = new Set(prev);
      if (isChecked) {
        nextSet.add(taskName);
      } else {
        nextSet.delete(taskName);
      }
      return STANDARD_TEMPLATE_TASKS.filter(function(name) { return nextSet.has(name); });
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>
            {isEditing ? 'Modifica Progetto' : 'Nuovo Progetto'}
          </DialogTitle>
          <DialogDescription>
            {isEditing ? 'Modifica i dettagli del progetto' : 'Inserisci i dettagli del nuovo progetto'}
          </DialogDescription>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          <div className="space-y-2">
            <Label htmlFor="name">Nome Progetto</Label>
            <Input
              id="name"
              value={formData.name}
              onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
              placeholder="Es: Website Redesign"
              data-testid="project-name-input"
            />
          </div>

          {macroProjects && macroProjects.length > 0 && (
            <div className="space-y-2">
              <Label>Macro Progetto</Label>
              <select
                value={formData.macroProjectId}
                onChange={(e) => setFormData(prev => ({ ...prev, macroProjectId: e.target.value }))}
                className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
                data-testid="project-macro-select"
              >
                <option value="">-- Nessun Macro Progetto --</option>
                {macroProjects.map((mp) => (
                  <option key={mp.id} value={mp.id}>
                    {mp.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="saleAmount">Importo venduto (EUR)</Label>
            <Input
              id="saleAmount"
              type="number"
              min="0"
              step="0.01"
              value={formData.saleAmount}
              onChange={(e) => setFormData(prev => ({ ...prev, saleAmount: e.target.value }))}
              placeholder="Es: 25000"
              data-testid="project-sale-amount-input"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="estimatedRealDays">Giornate reali stimate</Label>
            <Input
              id="estimatedRealDays"
              type="number"
              min="0"
              step="0.25"
              value={formData.estimatedRealDays}
              onChange={(e) => setFormData(prev => ({ ...prev, estimatedRealDays: e.target.value }))}
              placeholder="Es: 40"
              data-testid="project-estimated-real-days-input"
            />
          </div>

          {showTemplateOptions && (
            <div className="space-y-2">
              <Label>Template Progetto</Label>
              <select
                value={templateType}
                onChange={(e) => setTemplateType(e.target.value)}
                className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
                data-testid="project-template-select"
              >
                <option value="standard">Template Standard</option>
                <option value="custom">Template Custom</option>
              </select>
              {templateType === 'standard' && (
                <p className="text-xs text-muted-foreground">
                  Verranno creati {STANDARD_TEMPLATE_TASKS.length} task standard.
                </p>
              )}
            </div>
          )}

          {showTemplateOptions && templateType === 'custom' && (
            <div className="space-y-3 rounded-lg border border-border p-3 bg-muted/20">
              <div className="flex items-center justify-between gap-2">
                <Label>Task da creare</Label>
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => setCustomTemplateTasks(STANDARD_TEMPLATE_TASKS)}
                  >
                    Tutti
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => setCustomTemplateTasks([])}
                  >
                    Nessuno
                  </Button>
                </div>
              </div>
              <div className="space-y-2">
                {STANDARD_TEMPLATE_TASKS.map(function(taskName, index) {
                  const id = 'template-task-' + index;
                  const checked = customTemplateTasks.indexOf(taskName) !== -1;
                  return (
                    <label key={taskName} htmlFor={id} className="flex items-center gap-2 text-sm">
                      <Checkbox
                        id={id}
                        checked={checked}
                        onCheckedChange={function(nextChecked) {
                          handleToggleCustomTask(taskName, !!nextChecked);
                        }}
                      />
                      <span>{taskName}</span>
                    </label>
                  );
                })}
              </div>
              {customTemplateInvalid && (
                <p className="text-xs text-destructive">
                  Seleziona almeno un task da creare.
                </p>
              )}
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Data Inizio</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "w-full justify-start text-left font-normal",
                      !formData.startDate && "text-muted-foreground"
                    )}
                    data-testid="project-start-date-btn"
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {formData.startDate ? format(parseISO(formData.startDate), 'dd/MM/yyyy') : 'Seleziona'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={formData.startDate ? parseISO(formData.startDate) : undefined}
                    onSelect={(date) => setFormData(prev => ({ 
                      ...prev, 
                      startDate: date ? format(date, 'yyyy-MM-dd') : '' 
                    }))}
                    locale={it}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>

            <div className="space-y-2">
              <Label>Data Fine</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "w-full justify-start text-left font-normal",
                      !formData.endDate && "text-muted-foreground"
                    )}
                    data-testid="project-end-date-btn"
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {formData.endDate ? format(parseISO(formData.endDate), 'dd/MM/yyyy') : 'Seleziona'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={formData.endDate ? parseISO(formData.endDate) : undefined}
                    onSelect={(date) => setFormData(prev => ({ 
                      ...prev, 
                      endDate: date ? format(date, 'yyyy-MM-dd') : '' 
                    }))}
                    locale={it}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>
          </div>

          <div className="space-y-2">
            <Label>Colore</Label>
            <div className="flex gap-2 flex-wrap">
              {PROJECT_COLORS.map(color => (
                <button
                  key={color.value}
                  type="button"
                  className={cn(
                    "w-8 h-8 rounded-full transition-transform hover:scale-110",
                    formData.color === color.value && "ring-2 ring-offset-2 ring-primary"
                  )}
                  style={{ backgroundColor: color.value }}
                  onClick={() => setFormData(prev => ({ ...prev, color: color.value }))}
                  title={color.label}
                  data-testid={`color-${color.label}`}
                />
              ))}
            </div>
          </div>

          {isEditing && onDelete && !showDeleteConfirm && (
            <Button
              type="button"
              variant="outline"
              className="w-full text-destructive hover:text-destructive hover:bg-destructive/10"
              onClick={() => setShowDeleteConfirm(true)}
              data-testid="delete-project-btn"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Elimina Progetto
            </Button>
          )}

          {showDeleteConfirm && (
            <div className="p-4 border border-destructive/50 rounded-lg bg-destructive/5 space-y-3">
              <div className="flex items-start gap-2 text-destructive">
                <AlertTriangle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                <div className="text-sm">
                  <p className="font-medium">Confermi l'eliminazione?</p>
                  <p className="text-muted-foreground">
                    Verranno eliminati anche tutti i task associati.
                  </p>
                </div>
              </div>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="flex-1"
                  onClick={() => setShowDeleteConfirm(false)}
                >
                  Annulla
                </Button>
                <Button
                  type="button"
                  variant="destructive"
                  size="sm"
                  className="flex-1"
                  onClick={handleDelete}
                  data-testid="confirm-delete-project-btn"
                >
                  Elimina
                </Button>
              </div>
            </div>
          )}

          <DialogFooter className="mt-6">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Annulla
            </Button>
            <Button 
              type="submit" 
              disabled={!formData.name || !formData.startDate || !formData.endDate || customTemplateInvalid}
              data-testid="save-project-btn"
            >
              {isEditing ? 'Salva Modifiche' : 'Crea Progetto'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default ProjectDialog;
